// The business profile (users/{uid}/businesses/{id}): the top of every
// invoice and the defaults for new ones. A port of business.dart.

import { addDays, type IsoDate } from '@/core/dates';
import {
  DEFAULT_DESIGN,
  designFromJson,
  type Invoice,
  type InvoiceDesign,
  NIGERIA_VAT,
  NO_DISCOUNT,
  NO_TAX,
  type TaxRate,
  taxFromJson,
} from '@/core/invoice';
import { currencyOf } from '@/core/money';

export interface BankDetails {
  bankName: string;
  accountName: string;
  accountNumber: string;
}

export interface Business {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  /** A small logo as a data URI. */
  logo: string | null;
  /** The owner's drawn signature as a PNG data URI. */
  signature: string | null;
  bank: BankDetails;
  /** ISO 4217 code. */
  currency: string;
  tax: TaxRate;
  /** Due date = issue date + this many days. */
  paymentTermsDays: number;
  invoicePrefix: string;
  nextInvoiceNumber: number;
  defaultNotes: string;
  design: InvoiceDesign;
  createdAt: string;
  updatedAt: string;
}

/** Logos live inside the business record, which holds at most 1 MB. */
export const MAX_LOGO_BYTES = 200 * 1024;

export const bankIsEmpty = (bank: BankDetails) => !bank.bankName && !bank.accountNumber;

type Json = Record<string, unknown>;
const str = (v: unknown, fallback = '') => (typeof v === 'string' ? v : fallback);
const int = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : fallback);
const obj = (v: unknown): Json | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : null);

export function businessFromJson(json: Json): Business {
  const id = str(json.id);
  if (!id) throw new Error('Not a business');
  const bank = obj(json.bank);
  return {
    id,
    name: str(json.name),
    email: str(json.email),
    phone: str(json.phone),
    address: str(json.address),
    logo: typeof json.logo === 'string' ? json.logo : null,
    signature: typeof json.signature === 'string' ? json.signature : null,
    bank: { bankName: str(bank?.bankName), accountName: str(bank?.accountName), accountNumber: str(bank?.accountNumber) },
    currency: currencyOf(str(json.currency, 'NGN')).code,
    tax: taxFromJson(obj(json.tax)),
    paymentTermsDays: int(json.paymentTermsDays, 14),
    invoicePrefix: str(json.invoicePrefix, 'INV-'),
    nextInvoiceNumber: int(json.nextInvoiceNumber, 1),
    defaultNotes: str(json.defaultNotes),
    design: designFromJson(obj(json.design)),
    createdAt: str(json.createdAt),
    updatedAt: str(json.updatedAt),
  };
}

export function businessToJson(b: Business): Json {
  return {
    id: b.id,
    name: b.name,
    email: b.email,
    phone: b.phone,
    address: b.address,
    logo: b.logo,
    signature: b.signature,
    bank: { ...b.bank },
    currency: b.currency,
    tax: { ...b.tax },
    paymentTermsDays: b.paymentTermsDays,
    invoicePrefix: b.invoicePrefix,
    nextInvoiceNumber: b.nextInvoiceNumber,
    defaultNotes: b.defaultNotes,
    design: { ...b.design },
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
  };
}

/** A new business with the app's defaults, as first-run setup makes it. */
export function newBusiness(args: { id: string; name: string; currency: string; now: string }): Business {
  return {
    id: args.id,
    name: args.name.trim(),
    email: '',
    phone: '',
    address: '',
    logo: null,
    signature: null,
    bank: { bankName: '', accountName: '', accountNumber: '' },
    currency: args.currency,
    tax: args.currency === 'NGN' ? NIGERIA_VAT : NO_TAX,
    paymentTermsDays: 14,
    invoicePrefix: 'INV-',
    nextInvoiceNumber: 1,
    defaultNotes: '',
    design: DEFAULT_DESIGN,
    createdAt: args.now,
    updatedAt: args.now,
  };
}

/** Two initials for the avatar when there is no logo. */
export function businessInitials(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter((w) => w.length > 0)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
  return letters || 'B';
}

/** "INV-0143" for 143. */
export function formatInvoiceNumber(business: Business, number: number): string {
  return `${business.invoicePrefix}${String(number).padStart(4, '0')}`;
}

/**
 * A new, empty draft on the business's defaults. The number is a preview
 * until the first save reserves one.
 */
export function draftInvoice(business: Business, args: { id: string; today: IsoDate; now: string }): Invoice {
  return {
    id: args.id,
    businessId: business.id,
    number: formatInvoiceNumber(business, business.nextInvoiceNumber),
    clientId: null,
    client: { name: '', email: '', phone: '', address: '' },
    currency: business.currency,
    issueDate: args.today,
    dueDate: addDays(args.today, business.paymentTermsDays),
    items: [],
    discount: NO_DISCOUNT,
    tax: business.design.showTax ? business.tax : NO_TAX,
    notes: business.defaultNotes,
    payments: [],
    design: business.design,
    sentAt: null,
    publicLinkId: null,
    createdAt: args.now,
    updatedAt: args.now,
  };
}

/** Oldest first: the first business set up leads. */
export function sortBusinesses(all: Business[]): Business[] {
  return [...all].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** The business in use among [all] (oldest first): the chosen one while it exists, else the oldest. */
export function activeBusinessOf(all: Business[], chosenId: string | null): Business | null {
  return all.find((b) => b.id === chosenId) ?? all[0] ?? null;
}

/**
 * Whether a record made for [businessId] belongs to [active]. Records from
 * before there could be several have no business, and belong to the first
 * ([primaryId]). As BusinessScope.owns in the phone app.
 */
/** The business a record made for [businessId] belongs to among [all]
 * (oldest first): records with no business, or one since removed, belong
 * to the oldest. */
export function ownerOf(all: Business[], businessId: string | null | undefined): Business | null {
  return all.find((b) => b.id === businessId) ?? all[0] ?? null;
}

/** The records among [records] that belong to the business [id]. */
export function recordsOf<T>(records: T[], all: Business[], id: string, businessOf: (record: T) => string | null | undefined): T[] {
  return records.filter((r) => ownerOf(all, businessOf(r))?.id === id);
}

export function ownedBy(businessId: string | null | undefined, active: Business | null, primaryId: string | null): boolean {
  if (!active) return true;
  if (!businessId) return active.id === primaryId;
  return businessId === active.id;
}

/**
 * Businesses a free plan can have: the first and one more. More needs a
 * real Pro plan; unlike other Pro features this limit holds before Pro
 * launches too, so nobody piles up businesses Pro would later take away.
 */
export const FREE_BUSINESS_LIMIT = 2;

/** Whether another business can be added (the phone app's BusinessesState.canAdd). */
export const canAddBusiness = (count: number, isPro: boolean) => isPro || count < FREE_BUSINESS_LIMIT;
